// Public "Meet the vendors" roster on an event's detail page. Fetches the
// approved-vendors endpoint and renders vendors grouped by the organizer's
// booth categories. Renders nothing when the organizer hasn't opted-in
// (server returns empty groups) or when there are no approved vendors.
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Tag, MapPin } from "lucide-react";
import api from "@/lib/api";

export default function ApprovedVendorsRoster({ eventId }) {
  const [state, setState] = useState(null);

  useEffect(() => {
    if (!eventId) return;
    let alive = true;
    api.get(`/events/${eventId}/approved-vendors`).then(({ data }) => {
      if (alive) setState(data);
    }).catch(() => { if (alive) setState({ groups: [], total: 0 }); });
    return () => { alive = false; };
  }, [eventId]);

  if (!state || !state.groups || state.groups.length === 0) return null;
  if (!state.show_booth_categories) return null;

  return (
    <section
      className="mt-10 rounded-sm p-6 sm:p-8"
      style={{
        background: "linear-gradient(135deg, rgba(107,47,186,0.05), rgba(44,196,217,0.05))",
        border: "1px solid rgba(107,47,186,0.15)",
      }}
      data-testid="public-vendor-roster"
    >
      <div className="overline mb-2" style={{ color: "var(--avt-purple)" }}>
        Meet the vendors · {state.total}
      </div>
      <h2 className="font-serif text-3xl sm:text-4xl">
        Who you&rsquo;ll meet at this event.
      </h2>
      <p className="mt-2 text-sm" style={{ color: "var(--ink-soft)" }}>
        The organizer grouped every approved booth so you can browse the lineup.
      </p>

      <div className="mt-8 space-y-8">
        {state.groups.map((g) => (
          <div
            key={g.category}
            data-testid={`public-roster-group-${g.category.toLowerCase().replace(/\s+/g, "-")}`}
          >
            <div className="flex items-center gap-2 mb-4">
              <span
                className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-widest px-3 py-1 rounded-full"
                style={{
                  background: g.category === "Miscellaneous"
                    ? "rgba(0,0,0,0.06)"
                    : "rgba(107,47,186,0.16)",
                  color: g.category === "Miscellaneous"
                    ? "var(--ink-soft)"
                    : "var(--avt-purple)",
                }}
              >
                <Tag size={11} /> {g.category} · {g.count}
              </span>
            </div>
            <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-3">
              {g.vendors.map((v) => {
                const cardInner = (
                  <div
                    className="flex items-center gap-3 p-3 rounded-sm border transition"
                    style={{
                      borderColor: "rgba(0,0,0,0.08)",
                      background: "#fff",
                    }}
                  >
                    {v.vendor_avatar_url ? (
                      <img
                        src={v.vendor_avatar_url}
                        alt=""
                        className="h-11 w-11 rounded-full object-cover flex-shrink-0"
                      />
                    ) : (
                      <div
                        className="h-11 w-11 rounded-full flex items-center justify-center flex-shrink-0 text-white font-bold"
                        style={{ background: "var(--avt-purple)" }}
                      >
                        {(v.vendor_name || "?").slice(0, 1).toUpperCase()}
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <div
                        className="font-bold text-sm truncate"
                        data-testid={`roster-vendor-${v.application_id}`}
                      >
                        {v.vendor_name}
                      </div>
                      {v.vendor_city && (
                        <div
                          className="text-[11px] mt-0.5 inline-flex items-center gap-1"
                          style={{ color: "var(--ink-soft)" }}
                        >
                          <MapPin size={9} /> {v.vendor_city}
                        </div>
                      )}
                    </div>
                  </div>
                );
                return v.vendor_slug ? (
                  <Link
                    key={v.application_id}
                    to={`/vendors/${v.vendor_slug}`}
                    className="hover:opacity-80"
                  >
                    {cardInner}
                  </Link>
                ) : (
                  <div key={v.application_id}>{cardInner}</div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
